import React, { useId, useMemo, useRef } from 'react';
import { useTranslation } from 'next-i18next';

import { usePdp } from '@context/PdpContext';
import { useStoresUI } from '@context/StoresContext';
import { useUI } from '@context/GlobalUIContext';

import PdpStoreItem from './StoreItem';
import PdpStoreList from './StoreList';
import SimpleButton from '_components/UI/SimpleButton';
import { filterStoresForPdpPreview } from './filterStoresForPdpPreview';
import { formatStorePreviewDetails } from './formatStorePreviewDetails';
import { sortPreviewStoresByShopperGeolocation } from './sortPreviewStoresByShopperGeolocation';
import { trackStoreAvailabilityModalOpenEvent } from '@utils/gtm/ga4/trackStoreAvailabilityModalEvents';

import styles from './FindInStoreSection.module.scss';

const selectVariantCopyKey = (
  colourCount: number,
  sizeCount: number
):
  | 'product.findInStore.selectVariant.sizeAndColour'
  | 'product.findInStore.selectVariant.colour'
  | 'product.findInStore.selectVariant.size' => {
  const selectedSizeAndColour = colourCount > 1 && sizeCount > 1;
  const selectColor = colourCount > 1 && sizeCount === 1;
  if (selectedSizeAndColour) {
    return 'product.findInStore.selectVariant.sizeAndColour';
  }
  if (selectColor) {
    return 'product.findInStore.selectVariant.colour';
  }
  return 'product.findInStore.selectVariant.size';
};

const FindInStoreSection = () => {
  const { t } = useTranslation('pdp');
  const titleId = useId().replace(/:/g, '');
  const { openStockLevelModal, openStoreInformationModal } = useUI();
  const { stores, favouriteStoreDetails } = useStoresUI();
  const { isGiftCard, selectedVariant, colours, sizes, shopperGeolocation } =
    usePdp();

  const colourCount = colours?.length ?? 0;
  const sizeCount = sizes?.length ?? 0;

  const favouriteStoreId = favouriteStoreDetails?.data?.mec_store_id;
  const hasFavouriteStore = Boolean(
    favouriteStoreDetails && favouriteStoreDetails !== false && favouriteStoreId
  );

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const previewStores = useMemo(() => {
    const fav =
      favouriteStoreDetails && favouriteStoreDetails !== false
        ? favouriteStoreDetails
        : undefined;
    return filterStoresForPdpPreview(stores, fav);
  }, [stores, favouriteStoreDetails]);

  const sortedPreviewStores = useMemo(
    () =>
      sortPreviewStoresByShopperGeolocation(previewStores, shopperGeolocation),
    [previewStores, shopperGeolocation]
  );

  const handleCheckAll = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    trackStoreAvailabilityModalOpenEvent({
      storeName: favouriteStoreDetails?.data?.store_name ?? '',
    });
    openStoreInformationModal();
  };

  const listItems = useMemo(() => {
    if (!hasFavouriteStore || !selectedVariant || !sortedPreviewStores.length) {
      return null;
    }

    return sortedPreviewStores.map((store, index) => {
      const id = store.data.mec_store_id;
      const inv = selectedVariant.storeInventory[id];
      const quantity =
        inv && typeof inv.quantity === 'number' ? inv.quantity : null;
      const { line } = formatStorePreviewDetails(store, t, shopperGeolocation);

      return (
        <PdpStoreItem
          key={id}
          storeName={store.data.store_name}
          detailsLine={line}
          quantity={quantity}
          className={index > 0 ? styles.listItemDivider : undefined}
        />
      );
    });
  }, [
    hasFavouriteStore,
    selectedVariant,
    sortedPreviewStores,
    shopperGeolocation,
    t,
  ]);

  if (isGiftCard) {
    return null;
  }

  let emptyState: React.ReactNode = null;

  if (!hasFavouriteStore) {
    emptyState = (
      <p className={styles.emptyState}>
        {t('product.findInStore.selectStore.part1')}
        <SimpleButton
          type="button"
          className={styles.inlineAction}
          onClick={() => openStockLevelModal()}
          variant="secondary"
        >
          {t('product.findInStore.selectStore.link')}
        </SimpleButton>
        {t('product.findInStore.selectStore.part2')}
      </p>
    );
  } else if (!selectedVariant && (colourCount > 1 || sizeCount > 1)) {
    emptyState = (
      <p className={styles.emptyState}>
        {t(selectVariantCopyKey(colourCount, sizeCount))}
      </p>
    );
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <PdpStoreList
        titleId={titleId}
        title={t('product.findInStore.title')}
        checkAllStoresLabel={t('product.findInStore.checkAllStores')}
        onCheckAllStoresClick={handleCheckAll}
        emptyState={emptyState}
      >
        {!emptyState ? listItems : null}
      </PdpStoreList>
    </div>
  );
};

export default FindInStoreSection;
